import React, { useState, useEffect } from 'react';
import { FirebaseUser } from '../types/user';
import { db } from '../services/firebase';
import { doc, updateDoc, deleteField } from 'firebase/firestore';

interface SpecialityManagementModalProps {
  user: FirebaseUser;
  specialty: string;
  onClose: () => void;
  onUpdate: () => void; // Callback to refresh parent data
}

interface SpecialtyData {
  subscription: string;
  allowance: number;
  expiry: Date;
}

const SpecialityManagementModal: React.FC<SpecialityManagementModalProps> = ({ 
  user, 
  specialty, 
  onClose, 
  onUpdate 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<SpecialtyData>({
    subscription: '',
    allowance: 0,
    expiry: new Date()
  });

  // Disable background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Initialize form data from user object
  useEffect(() => {
    // Get subscription
    let subscription = '';
    if (user.specialtySubscriptions) {
      if (typeof user.specialtySubscriptions.get === 'function') {
        subscription = String(user.specialtySubscriptions.get(specialty) || '');
      } else {
        subscription = String((user.specialtySubscriptions as any)[specialty] || '');
      }
    }

    // Get allowance
    let allowance = 0;
    if (user.interviewAllowance) {
      if (typeof user.interviewAllowance.get === 'function') {
        allowance = user.interviewAllowance.get(specialty) || 0;
      } else {
        allowance = (user.interviewAllowance as any)[specialty] || 0;
      }
    }

    // Get expiry
    let expiry = new Date();
    if (user.unlockedSpecialtyExpiries) {
      let expiryRaw;
      if (typeof user.unlockedSpecialtyExpiries.get === 'function') {
        expiryRaw = user.unlockedSpecialtyExpiries.get(specialty);
      } else {
        expiryRaw = (user.unlockedSpecialtyExpiries as any)[specialty];
      }

      if (typeof expiryRaw === 'string') {
        // Handle ISO string format
        const parsedDate = new Date(expiryRaw);
        if (!isNaN(parsedDate.getTime())) {
          expiry = parsedDate;
        }
      } else if (expiryRaw && typeof expiryRaw === 'object' && typeof expiryRaw.toDate === 'function') {
        // Handle legacy Timestamp format
        expiry = expiryRaw.toDate();
      } else if (expiryRaw instanceof Date) {
        expiry = expiryRaw;
      }
    }

    setFormData({
      subscription: String(subscription),
      allowance: Number(allowance),
      expiry
    });
  }, [user, specialty]);

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Prepare update data
      const updateData: any = {};
      
      // Update subscription
      updateData[`specialtySubscriptions.${specialty}`] = formData.subscription;
      
      // Update allowance
      updateData[`interviewAllowance.${specialty}`] = formData.allowance;
      
      // Update expiry - store as ISO string
      updateData[`unlockedSpecialtyExpiries.${specialty}`] = formData.expiry.toISOString();

      await updateDoc(userRef, updateData);
      
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating specialty:', err);
      setError('Failed to update specialty. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete the ${specialty} specialty for this user? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', user.uid);
      
      // Prepare delete data
      const updateData: any = {};
      
      // Remove from all relevant fields
      updateData[`specialtySubscriptions.${specialty}`] = deleteField();
      updateData[`interviewAllowance.${specialty}`] = deleteField();
      updateData[`unlockedSpecialtyExpiries.${specialty}`] = deleteField();
      updateData[`attempts.interviewsBySpecialty.${specialty}`] = deleteField();
      
      // Remove from unlockedSpecialties array
      const updatedSpecialties = user.unlockedSpecialties?.filter(spec => spec !== specialty) || [];
      updateData.unlockedSpecialties = updatedSpecialties;

      await updateDoc(userRef, updateData);
      
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error deleting specialty:', err);
      setError('Failed to delete specialty. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const formattedSpecialtyName = specialty.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: '#2d2d2d',
          border: '1px solid #404040',
          borderRadius: '8px',
          width: '90vw',
          maxWidth: '500px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          backgroundColor: '#404040',
          borderBottom: '1px solid #555',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{margin: 0, color: '#e0e0e0', fontSize: '20px'}}>
            Edit {formattedSpecialtyName}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '20px',
          backgroundColor: '#2d2d2d',
          overflow: 'auto'
        }}>
          {error && (
            <div style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '10px',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          )}

          <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            {/* Subscription Plan */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Subscription Plan
              </label>
              <input
                type="text"
                value={formData.subscription}
                onChange={(e) => setFormData(prev => ({...prev, subscription: e.target.value}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
                placeholder="Enter subscription plan"
              />
            </div>

            {/* Interview Allowance */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Interview Allowance
              </label>
              <input
                type="number"
                value={formData.allowance}
                onChange={(e) => setFormData(prev => ({...prev, allowance: Number(e.target.value)}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
                min="0"
                placeholder="Enter interview allowance"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '6px',
                color: '#e0e0e0',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Expiry Date & Time
              </label>
              <input
                type="datetime-local"
                value={formatDateForInput(formData.expiry)}
                onChange={(e) => setFormData(prev => ({...prev, expiry: new Date(e.target.value)}))}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #404040',
                  borderRadius: '4px',
                  color: '#e0e0e0',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px',
          backgroundColor: '#333',
          borderTop: '1px solid #555',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleDelete}
            disabled={isLoading}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Deleting...' : 'Delete Specialty'}
          </button>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: '#6c757d',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpecialityManagementModal;